import React from "react";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";

// Single dropdown per column: "Do Not Import" or one of the target fields, grouped into
// "Match by" (identifies the existing record) and "Update" (fields to write). An Update-mapped
// column also gets an "Apply" checkbox -- mapping a column doesn't force it to be written.
const MapColumnsStep = ({ headers, sampleRows, fieldCatalog, mappings, setMappings, loading, onBack, onContinue }) => {
  const matchFields = fieldCatalog.filter((f) => f.matchable);
  const updateFields = fieldCatalog.filter((f) => f.updatable);

  const mappingFor = (header) => mappings.find((m) => m.sourceColumn === header) || { sourceColumn: header, targetField: null, role: null, apply: true };

  const updateMapping = (header, changes) => {
    setMappings((prev) => {
      const next = prev.filter((m) => m.sourceColumn !== header);
      const current = mappingFor(header);
      next.push({ ...current, ...changes });
      return next;
    });
  };

  const handleTargetChange = (header, value) => {
    if (!value) {
      updateMapping(header, { targetField: null, role: null, apply: true });
      return;
    }
    const [role, targetField] = value.split(":");
    const descriptor = fieldCatalog.find((f) => f.key === targetField);
    updateMapping(header, {
      targetField,
      role,
      apply: role === "UPDATE" ? !(descriptor?.key === "email") : true, // email defaults off: shared-location contact info most requests don't intend to overwrite
    });
  };

  const matchCount = mappings.filter((m) => m.role === "MATCH" && m.targetField).length;
  const updateCount = mappings.filter((m) => m.role === "UPDATE" && m.targetField && m.apply).length;

  return (
    <div className="flex flex-col gap-fluid-3">
      <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
        For each column, choose whether it identifies the existing location ("Match by"), updates a
        field ("Update"), or should be skipped.
      </p>

      <div className="overflow-x-auto border border-gray-200 dark:border-acs-dark-border rounded-lg">
        <table className="min-w-full text-fluid-sm">
          <thead className="bg-gray-50 dark:bg-acs-dark-bg text-gray-500 dark:text-acs-dark-subtext">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Column</th>
              <th className="text-left px-3 py-2 font-semibold">Sample Values</th>
              <th className="text-left px-3 py-2 font-semibold w-64">Maps To</th>
              <th className="text-left px-3 py-2 font-semibold w-20">Apply</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-acs-dark-border">
            {headers.map((header) => {
              const mapping = mappingFor(header);
              const samples = sampleRows
                .map((row) => row[header])
                .filter((v) => v)
                .slice(0, 3);
              const sampleText = samples.length > 0 ? samples.join(", ") : "(blank in preview rows)";
              const selectValue = mapping.targetField ? `${mapping.role}:${mapping.targetField}` : "";
              return (
                <tr key={header} className="bg-white dark:bg-acs-dark-surface">
                  <td className="px-3 py-2 font-medium text-gray-800 dark:text-acs-dark-text">{header}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-acs-dark-subtext truncate max-w-[280px]" title={sampleText}>
                    {sampleText}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={selectValue}
                      onChange={(e) => handleTargetChange(header, e.target.value)}
                      className="w-full border border-gray-300 dark:border-acs-dark-border rounded-md px-2 py-1
                                 bg-white dark:bg-acs-dark-bg text-gray-800 dark:text-acs-dark-text"
                    >
                      <option value="">Do Not Import</option>
                      <optgroup label="Match by">
                        {matchFields.map((f) => (
                          <option key={f.key} value={`MATCH:${f.key}`}>
                            {f.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Update">
                        {updateFields.map((f) => (
                          <option key={f.key} value={`UPDATE:${f.key}`}>
                            {f.label}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {mapping.role === "UPDATE" && mapping.targetField && (
                      <input
                        type="checkbox"
                        checked={mapping.apply}
                        onChange={(e) => updateMapping(header, { apply: e.target.checked })}
                        className="w-4 h-4 accent-acs-primary-navy dark:accent-acs-accent"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
        <span>
          {matchCount} match field(s), {updateCount} update field(s) selected.
        </span>
      </div>

      <div className="flex justify-between">
        <Button text="Back" variant="outline" onClick={onBack} disabled={loading} />
        <Button
          text={loading ? "Checking..." : "Preview Changes"}
          onClick={onContinue}
          disabled={loading || matchCount === 0 || updateCount === 0}
          className="!w-auto whitespace-nowrap !px-fluid-4"
        />
      </div>
    </div>
  );
};

export default MapColumnsStep;
